import { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../lib/supabase";
import { Label } from "../ui/label";
import { useToast } from "../../hooks/use-toast";
import { EditableField } from "../action-item/EditableField";
import { Button } from "../ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import React from "react";

interface Profile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  updated_at?: string;
  created_at?: string;
}

export function ProfileSettings() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: user?.email || "",
  });

  useEffect(() => {
    if (user?.id) {
      fetchProfile();
    }
  }, [user]);

  const fetchProfile = async () => {
    try {
      if (!user?.id) return;

      const { data, error } = await supabase
        .from("profiles")
        .select("first_name, last_name")
        .eq("id", user.id)
        .maybeSingle();

      if (error) throw error;

      if (!data) {
        const { error: insertError } = await supabase
          .from("profiles")
          .insert({
            id: user.id,
            first_name: user?.user_metadata?.first_name || "",
            last_name: user?.user_metadata?.last_name || "",
            updated_at: new Date().toISOString(),
          } as Profile);

        if (insertError) throw insertError;

        setFormData({
          firstName: user?.user_metadata?.first_name || "",
          lastName: user?.user_metadata?.last_name || "",
          email: user?.email || "",
        });
      } else {
        setFormData({
          firstName: data?.first_name || "",
          lastName: data?.last_name || "",
          email: user?.email || "",
        });
      }
    } catch (error) {
      console.error("Error fetching or creating profile:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load profile data. Please try again.",
      });
    }
  };

  const handleUpdateProfile = async (field: string, value: string) => {
    try {
      const updates: Profile = {
        id: user?.id as string,
        updated_at: new Date().toISOString(),
      };

      if (field === 'firstName') updates.first_name = value;
      if (field === 'lastName') updates.last_name = value;

      const { error: profileError } = await supabase
        .from("profiles")
        .upsert(updates);

      if (profileError) throw profileError;

      toast({
        title: "Profile updated",
        description: "Your profile has been updated successfully.",
      });

      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    } catch (error: any) {
      console.error("Update error:", error);
      toast({
        variant: "destructive",
        title: "Error updating profile",
        description: error.message || "Failed to update profile",
      });
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold mb-4">Profile Settings</h2>
        <p className="text-muted-foreground">
          Manage your account settings and preferences.
        </p>
      </div>

      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="firstName">First name</Label>
            <EditableField
              value={formData.firstName}
              onChange={(value) => handleUpdateProfile('firstName', value)}
              placeholder="Enter your first name"
              showToast={false}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lastName">Last name</Label>
            <EditableField
              value={formData.lastName}
              onChange={(value) => handleUpdateProfile('lastName', value)}
              placeholder="Enter your last name"
              showToast={false}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <EditableField
            value={formData.email}
            onChange={() => {}}
            placeholder="Your email"
            className="bg-background"
          />
        </div>
      </div>

      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold mb-4">Danger Zone</h3>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive">Delete Account</Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete your
                account and remove your data from our servers.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction className="bg-destructive text-destructive-foreground">
                Delete Account
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}